/**
 * Admin-management-klient för Lyktan (S-04-15).
 *
 * Wrappar `/api/admin/users`-endpointerna (S-12-11d, S-04-15):
 *  - `GET /api/admin/users`         — listar admin-konton (aktiva + inaktiva)
 *  - `POST /api/admin/users`        — invite nytt admin-konto + tempo-lösenord
 *  - `DELETE /api/admin/users/{id}` — soft-deaktivera (audit-trail i `admin_actions`)
 *
 * Hard-rules som tillämpas:
 *  - Cookie-baserad auth via `credentials: 'include'`. Inga tokens i body
 *    eller localStorage/sessionStorage.
 *  - CSRF-header på alla muterande anrop (POST/DELETE) — speglar
 *    `harden_admin_csrf`-cookien (S-12-11c). GET-listning är inte
 *    CSRF-skyddad på backend (SameSite=Strict räcker för GET).
 *  - Inga fritext-fält i request-body. `username` (lower-case) och
 *    `email` är kategoriska identifierare, max 100 tecken klient-side
 *    (backend enforce:ar gränserna också).
 *  - Backend returnerar UUID som user-id — inga råa databas-ID:n.
 *  - Tempo-lösenordet visas EN gång i invite-modalen och försvinner från
 *    minnet vid modal-unmount. Lagras aldrig i sessionStorage/localStorage.
 *  - Felmeddelanden mappas till svenska användartexter; backend-felkoderna
 *    är opaka tokens och loggas inte vidare.
 *  - MFA-step-up är ett krav på muterande endpoints — backend returnerar
 *    `mfa_not_configured` om aktören saknar MFA. UI:t fångar det och
 *    visar MFA-gate-banner istället för actions.
 */

import { csrfHeader } from '../auth/csrf';

/**
 * Admin-konto i listan. Återspeglar [AdminUserItem] i backend.
 */
export type AdminUser = {
  /** Opaque user-UUID. Råa databas-ID:n läcker aldrig. */
  userId: string;
  /** Lower-case-username (kategorisk identifierare, ingen fritext). */
  username: string;
  /** Lower-case-e-post. Visas i tabellen, redigeras inte i UI. */
  email: string;
  /** false = soft-deactiverad. Backend bevarar raden + audit-trail. */
  isActive: boolean;
  /** true = aktiverad TOTP-secret finns. false = MFA inte uppsatt. */
  mfaEnabled: boolean;
  /** ISO-8601 timestamp för senast lyckade login, eller null. */
  lastLoginAt: string | null;
  /** ISO-8601 timestamp för när kontot skapades. */
  joinedAt: string;
};

export type AdminUserListResult =
  | { kind: 'ok'; items: AdminUser[] }
  | { kind: 'unauthorized' }
  | { kind: 'failure'; message: string };

/**
 * Resultat av invite-flödet. `tempPasswordOrLink` visas EN gång för
 * inviter och försvinner från memory när modal stängs. Lagras aldrig.
 *
 * `expiresAt` är `null` tills backend implementerar tids-utgång på
 * tempo-lösenord — UI:t visar då en informativ text istället för datum.
 */
export type InviteResult =
  | {
      kind: 'ok';
      userId: string;
      username: string;
      email: string;
      tempPasswordOrLink: string;
      expiresAt: string | null;
    }
  | { kind: 'unauthorized' }
  | { kind: 'mfa_not_configured' }
  | { kind: 'mfa_locked' }
  | { kind: 'invalid_mfa_code' }
  | { kind: 'duplicate_identifier' }
  | { kind: 'failure'; message: string };

export type DeactivateResult =
  | { kind: 'ok' }
  | { kind: 'unauthorized' }
  | { kind: 'mfa_not_configured' }
  | { kind: 'mfa_locked' }
  | { kind: 'invalid_mfa_code' }
  | { kind: 'self_deactivation_forbidden' }
  | { kind: 'not_found' }
  | { kind: 'failure'; message: string };

const API_BASE = '/api/admin/users';

function messageForError(code: string): string {
  switch (code) {
    case 'unauthorized':
      return 'Sessionen har gått ut. Logga in igen.';
    case 'forbidden':
      return 'Saknar behörighet att hantera admin-konton.';
    case 'invalid_request':
      return 'Ogiltig begäran. Kontrollera fälten och försök igen.';
    case 'invalid_mfa_code':
      return 'Felaktig MFA-kod. Försök igen.';
    case 'mfa_locked':
      return 'För många misslyckade MFA-försök. Vänta en stund och försök igen.';
    case 'mfa_not_configured':
      return 'Du måste aktivera tvåfaktor först. Gå till Konto → Aktivera tvåfaktor.';
    case 'duplicate_identifier':
      return 'Användarnamn eller e-post är redan registrerat.';
    case 'self_deactivation_forbidden':
      return 'Du kan inte inaktivera ditt eget admin-konto.';
    case 'not_found':
      return 'Admin-kontot kunde inte hittas eller är redan inaktivt.';
    case 'network':
      return 'Kunde inte nå servern. Kontrollera anslutningen.';
    default:
      return 'Något gick fel. Försök igen om en stund.';
  }
}

async function safeJson(response: Response): Promise<{ error?: string } | null> {
  try {
    return (await response.json()) as { error?: string };
  } catch {
    return null;
  }
}

type RawAdminUser = {
  userId?: unknown;
  username?: unknown;
  email?: unknown;
  isActive?: unknown;
  mfaEnabled?: unknown;
  lastLoginAt?: unknown;
  joinedAt?: unknown;
};

type RawListResponse = {
  items?: unknown;
};

function parseAdminUser(raw: RawAdminUser): AdminUser | null {
  if (
    typeof raw.userId !== 'string' ||
    typeof raw.username !== 'string' ||
    typeof raw.email !== 'string' ||
    typeof raw.isActive !== 'boolean' ||
    typeof raw.mfaEnabled !== 'boolean' ||
    (raw.lastLoginAt !== null && typeof raw.lastLoginAt !== 'string') ||
    typeof raw.joinedAt !== 'string'
  ) {
    return null;
  }
  return {
    userId: raw.userId,
    username: raw.username,
    email: raw.email,
    isActive: raw.isActive,
    mfaEnabled: raw.mfaEnabled,
    lastLoginAt: (raw.lastLoginAt ?? null) as string | null,
    joinedAt: raw.joinedAt,
  };
}

/**
 * GET /api/admin/users — listar admin-konton.
 *
 * Inkluderar både aktiva och soft-deaktiverade konton. Soft-deletade
 * (deleted_at IS NOT NULL) filtreras bort på backend och syns inte här.
 */
export async function listAdmins(): Promise<AdminUserListResult> {
  let response: Response;
  try {
    response = await fetch(API_BASE, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401 || response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (!response.ok) {
    const body = await safeJson(response);
    return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
  }

  let body: RawListResponse;
  try {
    body = (await response.json()) as RawListResponse;
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }

  if (!Array.isArray(body.items)) {
    return { kind: 'failure', message: messageForError('default') };
  }

  const items: AdminUser[] = [];
  for (const raw of body.items) {
    const parsed = parseAdminUser(raw as RawAdminUser);
    if (!parsed) {
      return { kind: 'failure', message: messageForError('default') };
    }
    items.push(parsed);
  }
  return { kind: 'ok', items };
}

type RawInviteResponse = {
  userId?: unknown;
  username?: unknown;
  email?: unknown;
  temporaryPassword?: unknown;
  mustChangePassword?: unknown;
};

/**
 * POST /api/admin/users — invite nytt admin-konto.
 *
 * Backend kräver MFA-step-up (TOTP-kod) från aktören. Klienten skickar
 * koden separat — den ska aldrig hamna i URL:en eller i en GET-request.
 *
 * Returnerar `tempPasswordOrLink` EN gång — UI:t visar det i en modal,
 * användaren kopierar manuellt och delar via säker kanal (Signal,
 * fysiskt). Vi lagrar inte värdet — det försvinner när modalen stängs.
 *
 * `expiresAt` är `null` i nuvarande backend-implementation — tempo-
 * lösenordet har inte tids-utgång på backend, bara `must_change_password=true`
 * vid första login. Klienten visar då bara texten "engångsanvändning"
 * istället för datum.
 */
export async function inviteAdmin(
  email: string,
  displayName: string,
  mfaCode: string,
): Promise<InviteResult> {
  // Backend förväntar sig `username` och `email` separat. `displayName`
  // mappas till `username` (kategorisk identifierare, lower-case-
  // normaliserat på backend). Vi skickar inga fritext-fält
  // (granskningsmönster #3).
  const body = JSON.stringify({
    username: displayName.trim().toLowerCase(),
    email: email.trim().toLowerCase(),
    mfaCode: mfaCode,
  });

  let response: Response;
  try {
    response = await fetch(API_BASE, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeader() },
      body,
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 401) {
    const errorBody = await safeJson(response);
    if (errorBody?.error === 'invalid_mfa_code') return { kind: 'invalid_mfa_code' };
    return { kind: 'unauthorized' };
  }
  if (response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (response.status === 409) {
    const errorBody = await safeJson(response);
    if (errorBody?.error === 'mfa_not_configured') return { kind: 'mfa_not_configured' };
    if (errorBody?.error === 'duplicate_identifier') return { kind: 'duplicate_identifier' };
    return { kind: 'failure', message: messageForError(errorBody?.error ?? 'default') };
  }
  if (response.status === 429) {
    return { kind: 'mfa_locked' };
  }
  if (!response.ok) {
    const errorBody = await safeJson(response);
    return { kind: 'failure', message: messageForError(errorBody?.error ?? 'default') };
  }

  let raw: RawInviteResponse;
  try {
    raw = (await response.json()) as RawInviteResponse;
  } catch {
    return { kind: 'failure', message: messageForError('default') };
  }

  if (
    typeof raw.userId !== 'string' ||
    typeof raw.username !== 'string' ||
    typeof raw.email !== 'string' ||
    typeof raw.temporaryPassword !== 'string'
  ) {
    return { kind: 'failure', message: messageForError('default') };
  }

  return {
    kind: 'ok',
    userId: raw.userId,
    username: raw.username,
    email: raw.email,
    tempPasswordOrLink: raw.temporaryPassword,
    expiresAt: null,
  };
}

/**
 * DELETE /api/admin/users/{id} — soft-deactivera admin-konto.
 *
 * Backend kräver MFA-step-up från aktören och förbjuder själv-
 * deaktivering (returneras som `self_deactivation_forbidden`). Soft-
 * deactivation bevarar raden i `users` och loggar `admin_deactivate`
 * i `admin_actions`.
 */
export async function deactivateAdmin(userId: string, mfaCode: string): Promise<DeactivateResult> {
  const body = JSON.stringify({ mfaCode });

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeader() },
      body,
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }

  if (response.status === 204) {
    return { kind: 'ok' };
  }
  if (response.status === 400) {
    const errorBody = await safeJson(response);
    if (errorBody?.error === 'self_deactivation_forbidden') {
      return { kind: 'self_deactivation_forbidden' };
    }
    return { kind: 'failure', message: messageForError(errorBody?.error ?? 'default') };
  }
  if (response.status === 401) {
    const errorBody = await safeJson(response);
    if (errorBody?.error === 'invalid_mfa_code') return { kind: 'invalid_mfa_code' };
    return { kind: 'unauthorized' };
  }
  if (response.status === 403) {
    return { kind: 'unauthorized' };
  }
  if (response.status === 404) {
    return { kind: 'not_found' };
  }
  if (response.status === 409) {
    const errorBody = await safeJson(response);
    if (errorBody?.error === 'mfa_not_configured') return { kind: 'mfa_not_configured' };
    return { kind: 'failure', message: messageForError(errorBody?.error ?? 'default') };
  }
  if (response.status === 429) {
    return { kind: 'mfa_locked' };
  }
  if (!response.ok) {
    const errorBody = await safeJson(response);
    return { kind: 'failure', message: messageForError(errorBody?.error ?? 'default') };
  }
  return { kind: 'ok' };
}

/**
 * Användarvänlig svensk text för felkoder. Exporteras så att UI-vyn
 * kan rendera samma meddelande för olika fel-källor (t.ex. "MFA inte
 * konfigurerat" från både list-, invite- och deactivate-flödet).
 */
export function adminUserErrorMessage(code: string): string {
  return messageForError(code);
}
